
import React from "react";
import * as Hooks from "../../../hooks";
import { i18nManager } from "../../../i18n";
import { Schemes, Utils } from "../../../utils";
import { Elements } from "../../shared/Elements";
import * as ReactRouterDom from "react-router-dom";
import { ConfirmDialog, Siren } from "../../shared/ConfirmDialog";
import LoadingGif from "../../../assets/images/loading_circle.gif";
import { ApiResponseData, BaseService, ProgramService, IProgram, IMember, ITournamentEdition, SanitizedResponse, TournamentService, TournamentStatus, ITournamentEditionRegistered, RegisteredTournamentStatus } from "../../../services";
import { Alignment, BoolHelper, Button, ButtonManageRef, Checkbox, Classname, DateHelper, FormControl, MessageSchemesIcons, Scheme, StringHelper, TextAreaInput, TextInput, useScreenDimension, useSubscription } from "@rivtn/noseur";
import { buildJoinTournamentHeader, RegisterForTournament, showTournamentPass } from "./RegisterForTournament";

export interface ManageTournamentsProps {
    profile: IMember;
}

let fetching = false;

function ManageTournaments(props: ManageTournamentsProps) {

    const { profile } = props;
    const { width } = useScreenDimension();
    const [themeMode, _] = Hooks.useThemeMode();
    const navigate = ReactRouterDom.useNavigate();
    const labels = i18nManager.Labels.tournaments;
    const countryService = ProgramService.getInstance();
    const tournamentService = TournamentService.getInstance();
    const headerProgramsScrollView = React.useRef<HTMLDivElement>(null);
    const [headerPrograms, setHeaderPrograms] = React.useState<IProgram[]>();
    const [programs, setPrograms] = React.useState<ApiResponseData<IProgram>>({});
    const [tournamentWebsiteLink, setTournamentWebsiteLink] = React.useState<string>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [selectedProgramsIds, setSelectedProgramsIds] = React.useState<string[]>(Utils.getSetting("spids", []));
    const [section, setSection] = React.useState<TournamentStatus>((searchParams.get("section") as any) ?? TournamentStatus.UPCOMING);
    const [tournamentEditions, setTournamentEditions] = React.useState<ApiResponseData<ITournamentEdition | ITournamentEditionRegistered>>({});
    useSubscription("TOURNAMENT.REGISTRATION.FETCH", fetchTournaments);
    useSubscription("TOURNAMENT.REGISTRATION.STATE", (params) => {
        if (params.clear) {
            Utils.updateSearchParams("reg", null, setSearchParams);
            Utils.updateSearchParams("regtour", null, setSearchParams);
            Utils.updateSearchParams("regstatus", null, setSearchParams);
            Utils.updateSearchParams("regtourstate", null, setSearchParams);
            return;
        };
        Utils.updateSearchParams("reg", params.rid, setSearchParams);
        Utils.updateSearchParams("regtour", params.id, setSearchParams);
        Utils.updateSearchParams("regtourstate", params.state, setSearchParams);
    });

    React.useEffect(() => {
        if (document.documentElement.clientWidth < 800) {
            labels.navigators.your_tournaments = labels.navigators.your_tournaments.split(" ")[0];
            labels.navigators.past_tournaments = labels.navigators.past_tournaments.split(" ")[0];
            labels.navigators.ongoing_tournaments = labels.navigators.ongoing_tournaments.split(" ")[0];
            labels.navigators.upcoming_tournaments = labels.navigators.upcoming_tournaments.split(" ")[0];
        }
        countryService.queryPrograms({ sort: "no_of_tournaments,DESC" }).then(({ apiResponse }: SanitizedResponse<IProgram>) => {
            setHeaderPrograms(apiResponse.data!.content);
            setPrograms(apiResponse.data!);
        }).catch(BaseService.reportError);
    }, []);

    React.useEffect(() => {
        fetchTournaments();
    }, [section]);

    if (tournamentWebsiteLink) {
        return (<div className="tournaments-iframe">
            <Button scheme={Schemes.OMNEHOST} text={labels.back_to_tournament} onClick={() => {
                setTournamentWebsiteLink(undefined);
            }} />
            <iframe src={tournamentWebsiteLink} />
        </div>);
    }

    return (<div className="tournaments">
        <div className="header-programs">
            <div className="progs" ref={headerProgramsScrollView} onScroll={(e) => {

            }}>
                {!headerPrograms?.length && (<i className="fas fa-spinner fa-pulse" />)}
                {headerPrograms?.map((program) => {
                    const isSelected = selectedProgramsIds.includes(program.external_id);

                    return (<Button raised scheme={Schemes.OMNEHOST} key={program.external_id} text={program.name}
                        outlined={!isSelected} style={!isSelected ? { backgroundColor: "rgba(147, 203, 230, 0.1)" } : undefined}
                        onClick={() => toggleProgram(program)} />);
                })}
            </div>
            <Button borderless outlined iconOnly textOnly scheme={Schemes.OMNEHOST} rightIcon={"fa fa-angle-left"} onClick={() => {
                if (!headerProgramsScrollView.current) return;
                headerProgramsScrollView.current.scrollLeft = headerProgramsScrollView.current!.scrollLeft - 250;
            }} />
            <Button borderless outlined iconOnly textOnly scheme={Schemes.OMNEHOST} rightIcon={"fa fa-angle-right"} onClick={() => {
                if (!headerProgramsScrollView.current) return;
                headerProgramsScrollView.current.scrollLeft = headerProgramsScrollView.current!.scrollLeft + 250;
            }} />
            <Button borderless outlined iconOnly textOnly scheme={Schemes.OMNEHOST} rightIcon="fa fa-cog" onClick={searchPrograms} />
        </div>
        <div className="navigators">
            <div className={Classname.build({ "active": section === TournamentStatus.UPCOMING })} onClick={() => toggleSection(TournamentStatus.UPCOMING)}>{labels.navigators.upcoming_tournaments}</div>
            <div className={Classname.build({ "active": section === TournamentStatus.ONGOING })} onClick={() => toggleSection(TournamentStatus.ONGOING)}>{labels.navigators.ongoing_tournaments}</div>
            <div className={Classname.build({ "active": section === TournamentStatus.ENDED })} onClick={() => toggleSection(TournamentStatus.ENDED)}>{labels.navigators.past_tournaments}</div>
            <div className="divider" />
            <div className={Classname.build({ "active": section === TournamentStatus.REGISTERED })} onClick={() => toggleSection(TournamentStatus.REGISTERED)}>
                <span style={{ display: "flex" }}>{labels.navigators.your_tournaments} {Elements.buildPulsar({ marginLeft: 10, "--pulsarSize": "15px" } as any)}</span>
            </div>
        </div>
        <div className="pagination">
            {Elements.buildPaginator(tournamentEditions ?? {},
                ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchTournaments),
                parseInt(searchParams.get("page") ?? "1"), { marginLeft: 25, marginBottom: 5, marginTop: 15 })}
            <div style={{ display: "flex", marginTop: (width > 800 ? undefined : 15), marginLeft: (width > 800 ? undefined : 25) }}>
                <Checkbox scheme={Schemes.OMNEHOST} label={labels.online_tournaments} style={{ marginRight: 15 }} />
                {(width > 800) && Elements.buildSorter(labels.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchTournaments), labels.sorts[0].label, {})}
            </div>
        </div>
        <div className="tournaments-list">
            {!tournamentEditions.content && (<div style={{ margin: "auto", display: "flex" }}>
                <img alt={"loading tournament hosts"} src={LoadingGif} style={{ width: 50, margin: "auto" }} />
            </div>)}
            {tournamentEditions.content && !tournamentEditions.content?.length && (Elements.buildEmptyListView("fa fa-trophy", (<>
                <span>{labels.empty_label_parts.no_tournament_matches}
                    <a className="link" href="/hosts"> {labels.empty_label_parts.create}</a> {labels.empty_label_parts.or_update_search}</span>
            </>)))}
            {tournamentEditions.content && !tournamentEditions.content?.length || true && (tournamentEditions.content?.map((tournamentOrRegistered, index) => {
                let registeredTournament = ("tournament_edition" in tournamentOrRegistered ? tournamentOrRegistered : {}) as ITournamentEditionRegistered;
                let tournamentEdition = ("tournament_edition" in tournamentOrRegistered ? tournamentOrRegistered.tournament_edition : tournamentOrRegistered);
                const profileCurrency = i18nManager.getCountryCurrency(profile.country_code);
                const tournamentUpcomingAndRegistered = section === TournamentStatus.UPCOMING && tournamentEdition.registered;
                const tournamentEnded = DateHelper.isPast(new Date(tournamentEdition.dates.prize_pool_distribution_end_date ?? ""));
                const tournamentRegistrationEnded = DateHelper.isPast(new Date(tournamentEdition.dates.registration_end_date ?? ""));
                const tournamentRegistrationInProgress = DateHelper.isFuture(new Date(tournamentEdition.dates.registration_end_date ?? ""));
                const isWithDrawnOrRemoved = BoolHelper.equalsAny(registeredTournament?.status, [RegisteredTournamentStatus.WITHDRAWN, RegisteredTournamentStatus.REMOVED]);
                const unreadMessagesCount = (tournamentEdition.un_reads?.messages && tournamentEdition.un_reads?.messages > 9 ? "9+" : tournamentEdition.un_reads?.messages);
                const tournamentOngoing = DateHelper.isPast(new Date(tournamentEdition.dates.tournament_start_date ?? "")) && DateHelper.isFuture(new Date(tournamentEdition.dates.tournament_end_date ?? ""));
                const tournamentRegistrationOpen = DateHelper.isPast(new Date(tournamentEdition.dates.registration_start_date ?? "")) && DateHelper.isFuture(new Date(tournamentEdition.dates.registration_end_date ?? ""));
                const tournamentRegistrationDonePendingStart = DateHelper.isPast(new Date(tournamentEdition.dates.registration_end_date ?? "")) && DateHelper.isFuture(new Date(tournamentEdition.dates.tournament_start_date ?? ""));
                const tournamentRegistrationCurrency = ((tournamentEdition.registration && profileCurrency in tournamentEdition.registration) ? profileCurrency : Object.keys(tournamentEdition.registration ?? {})[0]);
                const tournamentEndedPendingResultAnnouncement = DateHelper.isPast(new Date(tournamentEdition.dates.tournament_end_date ?? "")) && DateHelper.isFuture(new Date(tournamentEdition.dates.result_announcement_date ?? ""));
                const tournamentPrizeCurrency = ((tournamentEdition.meta.price_pool_amounts && profileCurrency in tournamentEdition.meta.price_pool_amounts) ? profileCurrency : Object.keys(tournamentEdition.meta.price_pool_amounts ?? {})[0]);
                const tournamentEndedPendingPrizeDistribution = DateHelper.isPast(new Date(tournamentEdition.dates.tournament_end_date ?? "")) && DateHelper.isPast(new Date(tournamentEdition.dates.result_announcement_date ?? "")) && DateHelper.isFuture(new Date(tournamentEdition.dates.prize_pool_distribution_end_date ?? ""));
                const disableActionButton = (section === TournamentStatus.UPCOMING && (!tournamentRegistrationOpen || (tournamentEdition.registered || (tournamentEdition.meta.total_expected_participants > -1 && tournamentEdition.meta.total_registered_participants >= tournamentEdition.meta.total_expected_participants))));

                return (<div className={Classname.build("tournament", { "withdrawn": registeredTournament?.status === RegisteredTournamentStatus.WITHDRAWN, "removed": registeredTournament?.status === RegisteredTournamentStatus.REMOVED })} key={tournamentEdition.external_id + index} onClick={() => onTournamentClick(tournamentEdition, registeredTournament)}>
                    <div className="ba">
                        <img alt={tournamentEdition.name} src={tournamentEdition.banner_url} />
                        {(!!tournamentEdition.extra_data.show_prize_pool_in_listing) ? ((tournamentEdition.meta?.price_pool_monetary || tournamentEdition.meta?.price_pool_physical)
                            ? (<div className={Classname.build("prize", { "both": (tournamentEdition.meta?.price_pool_monetary && tournamentEdition.meta?.price_pool_physical) })}>
                                {(tournamentEdition.meta?.price_pool_monetary && tournamentEdition.meta.price_pool_amounts) && (<>
                                    {i18nManager.getCurrencySign(tournamentPrizeCurrency)} {StringHelper.toPresentableMoneyValue(tournamentEdition.meta.price_pool_amounts[tournamentPrizeCurrency])}
                                </>)}
                                {tournamentEdition.meta?.price_pool_physical && (<i className="fa fa-gift" />)}
                            </div>)
                            : (<div className="no-prize"></div>)) : (<div style={{ background: "unset", boxShadow: "unset" }} />)}
                        {section === TournamentStatus.REGISTERED
                            ? (<div style={{ backgroundColor: "transparent", boxShadow: "unset", paddingRight: 0, display: "flex" }}>
                                {tournamentOngoing && (Elements.buildPulsar())}
                                {(isWithDrawnOrRemoved) && (<div className="noseur-badge-overlay" style={{ cursor: "pointer", marginTop: -15 }}>
                                    {(!!unreadMessagesCount) && (<span className="noseur-badge noseur-danger noseur-no-border" style={{ right: 0 }}>{unreadMessagesCount}</span>)}
                                    <Button leftIcon={<i className="fa fa-message" style={{ fontSize: 22 }} />} textOnly rounded scheme={Schemes.OMNEHOST} />
                                </div>)}
                            </div>)
                            : (<div className="ttype">
                                <i className="fas fa-users" />
                                {i18nManager.Labels.teams.team_type_presentable[tournamentEdition.team_type.toLowerCase()]}
                            </div>)}
                    </div>
                    <img className="logo" alt={tournamentEdition.name} src={tournamentEdition?.logo_url} />
                    <div className="info">
                        <span className="name">{tournamentEdition.name}</span>
                        {section === TournamentStatus.UPCOMING && (<span className="loc lined-limit">{labels.tournament.closes} {Utils.formatToFineDate2(tournamentEdition.dates.registration_end_date ?? "")}</span>)}
                        {section === TournamentStatus.ONGOING && (<span className="loc lined-limit">{labels.tournament.ends} {Utils.formatToFineDate2(tournamentEdition.dates.tournament_end_date ?? "")}</span>)}
                        {section === TournamentStatus.ENDED && (DateHelper.isFuture(new Date(tournamentEdition.dates.prize_pool_distribution_start_date ?? ""))
                            ? (<span className="loc lined-limit">{labels.tournament.announcement_on} {Utils.formatToFineDate2(tournamentEdition.dates.prize_pool_distribution_start_date ?? "")}</span>)
                            : (<span className="loc lined-limit">{labels.tournament.announced_on} {Utils.formatToFineDate2(tournamentEdition.dates.prize_pool_distribution_start_date ?? "")}</span>))}
                        {section === TournamentStatus.REGISTERED && (<>
                            <span className="lined-limit">
                                {labels.tournament.status + " "}
                                {(tournamentEnded) && (labels.tournament.statuses.ended)}
                                {tournamentOngoing && (labels.tournament.statuses.ongoing)}
                                {tournamentRegistrationInProgress && (labels.tournament.statuses.registration_in_progress)}
                                {tournamentEndedPendingResultAnnouncement && (labels.tournament.statuses.ended_pending_result_announcement)}
                                {tournamentEndedPendingPrizeDistribution && (labels.tournament.statuses.ended_pending_prize_pool_distribution)}
                                {tournamentRegistrationDonePendingStart && (labels.tournament.statuses.starting_at + " " + Utils.formatToFineDate2(tournamentEdition.dates.tournament_start_date ?? ""))}
                            </span>
                        </>)}
                        <div className="line-items">
                            <div>
                                <span>{labels.tournament.program}</span>
                                <span className="lined-limit">{tournamentEdition.program.name}</span>
                            </div>
                            {tournamentEdition.location && (<div>
                                <span>{labels.tournament.location}</span>
                                <span className="lined-limit">{tournamentEdition.location}</span>
                            </div>)}
                            {section === TournamentStatus.UPCOMING && (<div>
                                <span>{labels.tournament.registration_fee}</span>
                                {(!tournamentEdition.registration || !Object.keys(tournamentEdition.registration).length)
                                    ? (<span className="lined-limit">{labels.tournament.free}</span>)
                                    : (<span className="lined-limit">{i18nManager.getCurrencySign(tournamentRegistrationCurrency)} {StringHelper.toPresentableMoneyValue(tournamentEdition.registration[tournamentRegistrationCurrency].fee_amount)}</span>)}
                            </div>)}
                            {section === TournamentStatus.ONGOING && (<div>
                                <span>{labels.tournament.tournament_day}</span>
                                <span className="lined-limit">{DateHelper.getDaysBetweenDates(new Date(tournamentEdition.dates.tournament_start_date ?? ""), new Date(tournamentEdition.dates.tournament_end_date ?? ""))}</span>
                            </div>)}
                            {(section === TournamentStatus.ENDED && DateHelper.isFuture(new Date(tournamentEdition.dates.prize_pool_distribution_start_date ?? ""))) && (<div>
                                <span>{labels.tournament.prize_pool_distribution}</span>
                                <span className="lined-limit">{labels.tournament.pending}</span>
                            </div>)}
                            {(section === TournamentStatus.REGISTERED) && (<div>
                                <span>{labels.tournament.host}</span>
                                <span className="lined-limit link">{tournamentEdition.tournament.host?.name}</span>
                            </div>)}
                        </div>
                    </div>
                    <div className="controls">
                        {isWithDrawnOrRemoved
                            ? (<div className="parts-count">
                                <span>{registeredTournament?.status === RegisteredTournamentStatus.WITHDRAWN ? labels.tournament.withdraw_because : labels.tournament.removed_because}</span>
                                <span className="lined-limit">{registeredTournament.withdrawal_or_removal_reason}</span>
                            </div>)
                            : (<>
                                {section === TournamentStatus.REGISTERED
                                    ? (<Button scheme={Schemes.OMNEHOST} text={labels.tournament.pass} onClick={(e) => {
                                        e.stopPropagation();
                                        showTournamentPass(registeredTournament);
                                    }} />)
                                    : ((!!tournamentEdition.registration_settings.show_participants_count_on_listing)
                                        ? (<div className="parts-count">
                                            <span>{labels.tournament.participants_joined}</span>
                                            <span>{tournamentEdition.meta.total_registered_participants}{tournamentEdition.meta.total_expected_participants > -1 ? `/${tournamentEdition.meta.total_expected_participants}` : ""}</span>
                                        </div>)
                                        : <div />)}
                                {section === TournamentStatus.REGISTERED
                                    ? (tournamentEnded
                                        ? (<Button scheme={Schemes.OMNEHOST} text={labels.tournament.view_result} />)
                                        : (<Button scheme={Scheme.DANGER} disabled={tournamentRegistrationEnded} text={labels.tournament.withdraw} onClick={(e) => {
                                            e.stopPropagation();
                                            withdrawFromTournament(tournamentOrRegistered as ITournamentEditionRegistered);
                                        }} />))
                                    : (<Button raised scheme={Schemes.OMNEHOST} text={resolveTournamentActionButtonText(tournamentEdition)} disabled={disableActionButton}
                                        style={tournamentUpcomingAndRegistered ? { backgroundColor: "#EBF8FE" } : undefined} outlined={tournamentUpcomingAndRegistered} onClick={(e) => {
                                            e.stopPropagation();
                                            onClickTournamentActionButton(tournamentEdition);
                                        }} />)}
                            </>)}
                    </div>
                </div>);
            }))}
        </div>
    </div>);

    function resolveTournamentActionButtonText(tournamentEdition: ITournamentEdition) {
        if (section === TournamentStatus.ONGOING || (section === TournamentStatus.ENDED && DateHelper.isFuture(new Date(tournamentEdition.dates.prize_pool_distribution_start_date ?? "")))) return labels.tournament.visit;
        else if (section === TournamentStatus.ENDED) return labels.tournament.view_result;
        else if (tournamentEdition.registered) return labels.tournament.registered;
        return labels.tournament.register;
    }

    function onClickTournamentActionButton(tournamentEdition: ITournamentEdition) {
        if (section === TournamentStatus.ONGOING || (section === TournamentStatus.ENDED && DateHelper.isFuture(new Date(tournamentEdition.dates.prize_pool_distribution_start_date ?? "")))) {
            setTournamentWebsiteLink(Utils.buildPublicUrl("TOURNAMENT", tournamentEdition.tournament.public_id));
            return;
        }
        if (section === TournamentStatus.ENDED) {
            setTournamentWebsiteLink(Utils.buildPublicUrl("TOURNAMENT", tournamentEdition.tournament.public_id, "/result"));
            return;
        }
        RegisterForTournament(profile, { tournament_edition: tournamentEdition } as ITournamentEditionRegistered);
    }

    function toggleProgram(program: IProgram) {
        const programIndex = selectedProgramsIds.indexOf(program.external_id);
        if (programIndex > -1) {
            selectedProgramsIds.splice(programIndex, 1);
        } else {
            selectedProgramsIds.push(program.external_id);
        }
        Utils.updateSettings("spids", selectedProgramsIds);
        setSelectedProgramsIds([...selectedProgramsIds]);
    }

    function toggleSection(section: TournamentStatus) {
        setSection(section);
        Utils.updateSearchParams("section", section, setSearchParams);
    }

    function fetchTournaments() {
        if (fetching) return;
        setTournamentEditions({});
        fetching = true;
        const method = (section === TournamentStatus.REGISTERED ? tournamentService.queryTournamentEditionRegistereds : tournamentService.queryTournamentEditions).bind(tournamentService);
        method({
            __member_id__: profile.external_id,
            sort: searchParams.get("sort") ?? "",
            name: searchParams.get("name") ?? "",
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "10",
            description: searchParams.get("description") ?? "",
            tournament_name: searchParams.get("tournament_name") ?? "",
            status__in: [section, RegisteredTournamentStatus.WITHDRAWN, RegisteredTournamentStatus.REMOVED],
        }).then(async ({ apiResponse, content }: SanitizedResponse<ITournamentEdition | ITournamentEditionRegistered>) => {
            setTournamentEditions(apiResponse.data!);
            const ongoingRegistrationId = searchParams.get("reg");
            const ongoingTournamentRegistrationId = searchParams.get("regtour");
            if (ongoingRegistrationId || ongoingTournamentRegistrationId) {
                (new Promise(async (resolve, reject) => {
                    if (ongoingRegistrationId) {
                        try {
                            const { sanitized } = await tournamentService.getSingleTournamentEditionRegistered(ongoingRegistrationId);
                            resolve(sanitized);
                            return;
                        } catch (err) {
                            reject(err);
                        }
                    }
                    for (const tour of content) {
                        if (tour.external_id === ongoingTournamentRegistrationId) {
                            resolve({ tournament_edition: tour });
                            return;
                        }
                    }
                    try {
                        const { sanitized: tournament } = await tournamentService.getSingleTournamentEdition(ongoingTournamentRegistrationId ?? "");
                        resolve({ tournament_edition: tournament });
                    } catch (err) {
                        reject(err);
                    }
                })).then((registeredTournament: any) => {
                    const registrationStatus = searchParams.get("regstatus");
                    if (registeredTournament && registrationStatus) {
                        Siren.alert((registrationStatus === "success" ? labels.registration_successful : labels.registration_failed).replace("${name}", registeredTournament.tournament_edition?.name), (registrationStatus === "success" ? undefined : {
                            scheme: Scheme.DANGER,
                            icon: MessageSchemesIcons.INFO,
                        }));
                    }
                    RegisterForTournament(profile, registeredTournament as ITournamentEditionRegistered, searchParams.get("regtourstate") ?? undefined);
                }).catch(BaseService.reportError);
            }
        }).catch(BaseService.reportError).finally(() => {
            fetching = false;
        });
    }

    function onTournamentClick(tournamentEdition: ITournamentEdition, registered: ITournamentEditionRegistered) {
        if (BoolHelper.equalsAny(registered?.status, [RegisteredTournamentStatus.WITHDRAWN, RegisteredTournamentStatus.REMOVED])) {
            showWithdrawnFromTournament(registered);
        } else if (registered.status === RegisteredTournamentStatus.REGISTERED) {
            navigate(`/tournaments/${registered.external_id}`);
        }
    }

    function searchPrograms() {
        const dialogLabels = labels.dialog.programs;
        const dialog = ConfirmDialog({
            noControl: true,
            dismissibleModal: true,
            confirmScheme: Scheme.DANGER,
            style: { maxWidth: 815 },
            content: (<div className="programs-dialog">
                <div className="controls">
                    <div className="left">
                        <span className="link danger" onClick={() => {
                            Utils.updateSettings("spids", []);
                            setSelectedProgramsIds([]);
                        }}>{dialogLabels.reset_filter}</span>
                        <span className="link" style={{ marginTop: 5 }}>{dialogLabels.top_in_country.replace("${country}", profile.country_code)}</span>
                    </div>
                    <div className="right">
                        <span className="link" style={{ marginRight: 10 }} onClick={() => dialog.hide()}>{dialogLabels.done}</span>
                        <FormControl scheme={Schemes.OMNEHOST} leftContent={"fa fa-search"} style={{ minWidth: 250 }}>
                            <TextInput placeholder={dialogLabels.search_program} />
                        </FormControl>
                    </div>
                </div>
                <div className="progs">
                    {programs.content?.map((program) => {
                        const isSelected = selectedProgramsIds.includes(program.external_id);

                        return (<div className={Classname.build("prog", { "selected": isSelected })} key={program.external_id} onClick={() => toggleProgram(program)}>
                            <div className="l" style={{ backgroundImage: `url(${program.logo_urls.length ? program.logo_urls[0] : "default"})` }} />
                            <span className="n">{program.name}</span>
                            <span className="up">{program.upcoming_tournaments_count} {dialogLabels.upcoming_tournaments}</span>
                        </div>);
                    })}
                </div>
            </div>),
        });
        dialog.show();
    }

    function withdrawFromTournament(registeredTournament: ITournamentEditionRegistered) {
        let reason = "", tournamentName = "";
        const dialogLabels = labels.dialog.withdraw;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            style: { maxWidth: "90%" },
            confirmScheme: Scheme.DANGER,
            confirmStyle: { marginRight: 15 },
            contentStyle: { padding: 0, paddingBottom: 15 },
            content: (<div className="tournament-reg-cont">
                <div className="tournament-register" style={{ height: 730 }}>
                    {buildJoinTournamentHeader(profile, registeredTournament, true)}
                    <div className="content">
                        <div>
                            <i className={"fa fa-sign-out err"} style={{ border: 0 }} />
                            <span>{dialogLabels.title}</span>
                        </div>
                        <span dangerouslySetInnerHTML={{ __html: dialogLabels.desc }} style={{ marginBottom: 20 }} />
                        <FormControl isFieldset fill label={dialogLabels.tournament_name_input.title}>
                            <TextInput placeholder={registeredTournament.tournament_edition.name} scheme={Schemes.OMNEHOST} onInputComplete={(v) => {
                                dialogErrorRef.current!.innerText = " ";
                                tournamentName = v;
                            }} />
                        </FormControl>
                        <FormControl isFieldset fill label={dialogLabels.reason_input.title} style={{ marginTop: 15 }}>
                            <TextAreaInput scheme={Schemes.OMNEHOST} style={{ height: 150, width: "100%" }} onInputComplete={(v) => {
                                dialogErrorRef.current!.innerText = " ";
                                reason = v;
                            }} />
                        </FormControl>
                        <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
                    </div>
                </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentName !== registeredTournament.tournament_edition.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.tournaments.dialog.confirm_tournament.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                tournamentService.withdrawTournamentEditionRegistered(registeredTournament.external_id, reason).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchTournaments();
                        Siren.alert(dialogLabels.withdrawal_request_submitted);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function showWithdrawnFromTournament(registeredTournament: ITournamentEditionRegistered) {
        const dialogLabels = labels.dialog.withdrawn;
        const refundAmount = registeredTournament.refund_amount?.amount ?? 0;
        const currencySign = i18nManager.getCurrencySign(registeredTournament.refund_amount?.currency_code ?? "NGN");

        const dialog = ConfirmDialog({
            style: { maxWidth: "90%" },
            footerAlignment: Alignment.RIGHT,
            confirmStyle: { marginRight: 15 },
            confirmLabel: i18nManager.Labels.common.close,
            contentStyle: { padding: 0, paddingBottom: 15 },
            content: (<div className="tournament-reg-cont">
                <div className="tournament-register" style={{ height: 650 }}>
                    {buildJoinTournamentHeader(profile, registeredTournament, true)}
                    <div className="content">
                        <div>
                            <i className={"fa fa-sign-out err"} style={{ border: 0 }} />
                            <span style={{ fontSize: 16 }}>{registeredTournament.status === RegisteredTournamentStatus.WITHDRAWN ? dialogLabels.withdraw_title : dialogLabels.remove_title}</span>
                        </div>
                        {refundAmount > 0 && (<b dangerouslySetInnerHTML={{
                            __html: dialogLabels.reg_was_refunded
                                .replaceAll("${currency}", currencySign)
                                .replaceAll("${amount}", StringHelper.toPresentableMoneyValue(refundAmount ?? 0))
                                .replaceAll("${charge_fee}", StringHelper.toPresentableMoneyValue(registeredTournament.refund_amount?.charge_fee ?? 0))
                        }} style={{ marginBottom: 10 }} />)}
                        <span>
                            <span style={{ opacity: 0.5 }}>{dialogLabels.your_reason}</span> {registeredTournament.withdrawal_or_removal_reason}
                        </span>
                    </div>
                </div>
            </div>),
        });
        dialog.show();
    }

}

export default ManageTournaments;
